import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
const features = [{
  title: 'Vital Records Vault',
  description: 'Birth certificates, insurance policies, property deeds organized and protected.'
}, {
  title: 'Medical Command Center',
  description: 'Complete family health profiles, medications, allergies, physician contacts.'
}, {
  title: 'Analog Communications Plan',
  description: 'Phone trees, meeting points, out-of-area contacts that work without power.'
}, {
  title: '60-Second Grab List',
  description: 'Pre-prioritized evacuation checklist so nothing critical gets left behind.'
}];
export function SolutionSection() {
  return <section className="bg-white py-20 px-4 border-b border-gray-200" data-id="element-172">
      <div className="max-w-7xl mx-auto" data-id="element-173">
        <div className="mb-12" data-id="element-174">
          <span className="font-heading text-sm font-bold text-navy tracking-[0.2em] uppercase mb-2 block" data-id="element-175">
            The Solution
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy" data-id="element-176">
            What's Inside the 72-Hour Emergency Binder?
          </h2>
          <div className="w-24 h-1 bg-safety-red mt-6" data-id="element-177"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-178">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="order-2 lg:order-1" data-id="element-179">
            <div className="space-y-6" data-id="element-180">
              {features.map((feature, index) => <div key={index} className="flex items-start p-4 border border-gray-200 bg-light-gray/50 shadow-sm" data-id="element-181">
                  <CheckCircleIcon className="w-6 h-6 text-cta-green mt-1 mr-4 flex-shrink-0" data-id="element-182" />
                  <div data-id="element-183">
                    <h3 className="font-heading font-bold text-lg text-navy uppercase tracking-wide mb-1" data-id="element-184">
                      {feature.title}
                    </h3>
                    <p className="font-body text-gray-700" data-id="element-185">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="order-1 lg:order-2 flex justify-center" data-id="element-186">
            {/* CSS Illustration of Tabbed Binder Sections */}
            <div className="relative w-full max-w-md h-[500px] bg-gray-100 border-2 border-gray-300 shadow-document p-8 flex flex-col" data-id="element-187">
              <div className="absolute top-0 left-0 w-full h-2 bg-navy" data-id="element-188"></div>

              {/* Tabs */}
              <div className="absolute -right-4 top-12 space-y-2" data-id="element-189">
                <div className="w-6 h-16 bg-red-100 border border-red-300 rounded-r shadow-sm flex items-center justify-center" data-id="element-190">
                  <span className="font-heading text-[10px] font-bold text-red-800 -rotate-90" data-id="element-191">
                    VITAL
                  </span>
                </div>
                <div className="w-6 h-16 bg-blue-100 border border-blue-300 rounded-r shadow-sm flex items-center justify-center" data-id="element-192">
                  <span className="font-heading text-[10px] font-bold text-blue-800 -rotate-90" data-id="element-193">
                    MED
                  </span>
                </div>
                <div className="w-6 h-16 bg-green-100 border border-green-300 rounded-r shadow-sm flex items-center justify-center" data-id="element-194">
                  <span className="font-heading text-[10px] font-bold text-green-800 -rotate-90" data-id="element-195">
                    COMMS
                  </span>
                </div>
                <div className="w-6 h-16 bg-yellow-100 border border-yellow-300 rounded-r shadow-sm flex items-center justify-center" data-id="element-196">
                  <span className="font-heading text-[10px] font-bold text-yellow-800 -rotate-90" data-id="element-197">
                    EVAC
                  </span>
                </div>
              </div>

              {/* Document Content */}
              <div className="flex-1 border border-gray-300 bg-white p-6 shadow-sm" data-id="element-198">
                <div className="border-b-2 border-navy pb-4 mb-6 flex justify-between items-end" data-id="element-199">
                  <div data-id="element-200">
                    <h4 className="font-heading font-bold text-2xl text-navy uppercase tracking-widest" data-id="element-201">
                      Table of Contents
                    </h4>
                    <p className="font-heading text-xs text-gray-500 uppercase tracking-widest mt-1" data-id="element-202">
                      Document Ref: 72H-EB
                    </p>
                  </div>
                  <div className="w-12 h-12 border-2 border-safety-red rounded-full flex items-center justify-center opacity-80 rotate-12" data-id="element-203">
                    <span className="font-heading text-[8px] font-bold text-safety-red uppercase text-center leading-tight" data-id="element-204">
                      Official
                      <br data-id="element-205" />
                      Copy
                    </span>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-206">
                  {[{
                  label: 'Vital Records Vault',
                  page: '01'
                }, {
                  label: 'Medical Command Center',
                  page: '02'
                }, {
                  label: 'Analog Comms Plan',
                  page: '03'
                }, {
                  label: '60-Second Grab List',
                  page: '04'
                }, {
                  label: 'Emergency Contacts',
                  page: '05'
                }, {
                  label: 'Evacuation Routes',
                  page: '06'
                }].map(item => <div key={item.page} className="flex items-center" data-id="element-207">
                      <span className="font-heading text-sm text-navy font-bold flex-shrink-0" data-id="element-208">
                        {item.label}
                      </span>
                      <div className="flex-1 border-b border-dotted border-gray-400 mx-2" data-id="element-209"></div>
                      <span className="font-heading text-xs text-gray-500 font-bold flex-shrink-0" data-id="element-210">
                        {item.page}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}